import { Card } from 'rebass';
import PropTypes from 'prop-types';
import React, { useMemo, useState, createContext } from 'react';
import dynamic from 'next/dynamic';
import 'react-image-lightbox/style.css';

import { useSelector } from 'react-redux';
import PageMedium from './PageMedium';
import StoryPageFeedback from './StoryPageFeedback';
import useEntity from './hooks/useEntity';

const Carousel = dynamic(import('./Carousel'), { ssr: false });
const Lightbox = dynamic(import('./lightbox/Lightbox'), { ssr: false });

export const StoryPageContext = createContext({});

const StoryPageContextProvider = props => {
  const [currentMedia, setCurrentMedia] = useState(null);
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const value = useMemo(() => {
    return {
      currentMedia,
      setCurrentMedia,
      lightboxMedia,
      setLightboxMedia,
    };
  }, [currentMedia, lightboxMedia]);

  return <StoryPageContext.Provider value={value} {...props} />;
};

const useFinishedPageMedia = id => {
  const page = useEntity({ id, type: 'pages' });
  return useSelector(state =>
    (page.media || []).filter(medium => state[medium.type][medium.id].state === 'finished')
  );
};

export const usePageMedia = id => {
  const page = useEntity({ id, type: 'pages' });
  const media = useFinishedPageMedia(id);

  const pageMedia = useMemo(() => {
    const imageWithAudioCaption =
      media.length === 2 && media[0].type === 'images' && media[1].type === 'audios';

    if (!page['horizontal-orientation'] && !imageWithAudioCaption) return media;
    if (!media.filter(medium => ['videos', 'images'].includes(medium.type)).length > 1) {
      return media;
    }

    const isCaptionableWithText = medium => ['images', 'videos'].includes(medium.type);
    const isCaptionableWithAudio = medium => ['images'].includes(medium.type);

    return media
      .map((medium, index, array) => {
        if (medium.type === 'texts') {
          const prevMedium = array[index - 1] || {};
          return isCaptionableWithText(prevMedium) ? null : medium;
        }
        if (medium.type === 'audios') {
          const prevMedium = array[index - 1] || {};
          return isCaptionableWithAudio(prevMedium) ? null : medium;
        }
        if (isCaptionableWithText(medium) || isCaptionableWithAudio(medium)) {
          const nextMedium = array[index + 1] || {};
          if (nextMedium.type === 'texts' && isCaptionableWithText(medium))
            return { ...medium, captionTextId: nextMedium.id };
          if (nextMedium.type === 'audios' && isCaptionableWithAudio(medium))
            return { ...medium, captionAudioId: nextMedium.id };
          return medium;
        }
        return medium;
      })
      .filter(medium => !!medium);
  }, [page]);

  return pageMedia;
};

const StoryPage = ({ id }) => {
  const page = useEntity({ id, type: 'pages' });
  const pageMedia = usePageMedia(id);

  if (!pageMedia.length) return false;

  const isServer = typeof window === 'undefined';
  const shouldCarousel = pageMedia.length > 1 && page['horizontal-orientation'] && !isServer;

  const renderStoryPage = () =>
    pageMedia.map(medium => (
      <PageMedium
        id={medium.id}
        type={medium.type}
        captionTextId={medium.captionTextId}
        captionAudioId={medium.captionAudioId}
        isCarousel={shouldCarousel}
        key={medium.id}
      />
    ));

  return (
    <StoryPageContextProvider>
      <Lightbox pageId={id} />
      <Card
        bg="white"
        mx="auto"
        mb={4}
        borderRadius={0}
        border="1px solid"
        borderColor="border"
        boxShadow="rgba(0, 0, 0, 0.15) 0px 1px 1px 0px"
        css="position: relative;"
      >
        {shouldCarousel ? <Carousel>{renderStoryPage()}</Carousel> : renderStoryPage()}
        <StoryPageFeedback id={id} />
      </Card>
    </StoryPageContextProvider>
  );
};

StoryPage.propTypes = {
  id: PropTypes.string.isRequired,
};

export default StoryPage;
